import { useState } from 'react';

import { Stack, Text } from '@eluve/components';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import {
  DefaultDetailPanel,
  DefaultsOverviewPanel,
  EmptyState,
  EntitySelectorPanel,
  PanelHeader,
} from './components';
import {
  useTenantDefaultsSelection,
  useTenantPromptDefaultsData,
} from './hooks';

interface TenantPromptDefaultsTabProps {
  tenantId?: string;
}

export const TenantPromptDefaultsTab: React.FC<
  TenantPromptDefaultsTabProps
> = ({ tenantId: propsTenantId }) => {
  const paramsTenantId = useTenantIdFromParams();
  const tenantId = propsTenantId ?? paramsTenantId;

  if (!tenantId) {
    return (
      <Text size="md" color="secondary">
        No tenant ID provided
      </Text>
    );
  }

  return <TenantPromptDefaultsContent tenantId={tenantId} />;
};

const TenantPromptDefaultsContent: React.FC<{ tenantId: string }> = ({
  tenantId,
}) => {
  const {
    tenantDefaults,
    userDefaults,
    tenantName,
    llmOutputTypes,
    promptTemplates,
    outputTemplates,
    usersWithCounts,
    refetchDefaults,
  } = useTenantPromptDefaultsData(tenantId);

  const {
    selectedEntity,
    selectedOutputType,
    setSelectedOutputType,
    selectedDefaults,
    handleSelectEntity,
  } = useTenantDefaultsSelection({ tenantDefaults, userDefaults });

  const [dialogOpen, setDialogOpen] = useState(false);

  // Get entity info for panels
  const entityType = selectedEntity.type;
  const entityName =
    selectedEntity.type === 'org' ? tenantName : selectedEntity.userName;
  const entityUserId =
    selectedEntity.type === 'user' ? selectedEntity.userId : undefined;

  return (
    <Stack
      align="stretch"
      className="max-h-[70vh] min-h-[500px] rounded-lg border border-border-tertiary"
      direction="horizontal"
    >
      {/* Left Column - Entity Selector (20%) */}
      <Stack
        className="w-1/5 shrink-0 overflow-y-auto border-r border-border-tertiary"
        gap={2}
      >
        <EntitySelectorPanel
          users={usersWithCounts}
          selectedEntity={selectedEntity}
          onSelectEntity={handleSelectEntity}
          orgName={tenantName}
        />
      </Stack>

      {/* Middle Column - Defaults Overview (20%) */}
      <Stack
        className="w-1/5 shrink-0 overflow-y-auto border-r border-border-tertiary"
        gap={2}
      >
        <DefaultsOverviewPanel
          defaults={selectedDefaults}
          entityType={entityType}
          entityName={entityName}
          onAddClick={() => setDialogOpen(true)}
          selectedOutputType={selectedOutputType}
          onSelectOutputType={setSelectedOutputType}
          selectedUserId={entityUserId}
          tenantId={tenantId}
          promptTemplates={promptTemplates}
          outputTemplates={outputTemplates}
        />
      </Stack>

      {/* Right Column - Detail View (60%) */}
      <Stack className="w-3/5 shrink-0 overflow-y-auto" gap={2}>
        {selectedOutputType || dialogOpen ? (
          <DefaultDetailPanel
            defaults={selectedDefaults}
            entityType={entityType}
            tenantId={tenantId}
            userId={entityUserId}
            llmOutputTypes={llmOutputTypes}
            promptTemplates={promptTemplates}
            outputTemplates={outputTemplates}
            onRefreshTemplates={() => refetchDefaults()}
            selectedOutputType={selectedOutputType}
            dialogOpen={dialogOpen}
            onOpenDialog={() => setDialogOpen(true)}
            onDialogOpenChange={setDialogOpen}
          />
        ) : (
          <Stack align="stretch" fullHeight>
            <PanelHeader title="Details" cornerRadius="tr" />
            <EmptyState
              message="Select an output type to view details"
              centered
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
